import {
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import {
  Button,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AccessControlAction from '../../components/AccessControlAction'
import TableComponent from '../../components/Common/TableComponent'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { systemName } from '../../config'
import { ACL_ACTION_TYPE } from '../../constants'
import { formatContractDate } from '../../date_format'
import DashboardLayout from '../../layouts/DashboardLayout'
import utils from '../../utils'
import { ContractManagementRequest, mapApiContractToUi } from './contractApi'
import {
  CONTRACT_STATUS_MAP,
  CONTRACT_STATUS_OPTIONS,
  formatCurrencyVN,
} from './mockData'

const { Option } = Select

const getStatusInfo = status => {
  const normalizedStatus = String(status || '').toLowerCase()
  return (
    CONTRACT_STATUS_MAP[normalizedStatus] || {
      label: String(status || 'Khong xac dinh'),
      color: 'default',
    }
  )
}

const ContractManagementListPage = props => {
  const { history } = props

  const [contracts, setContracts] = useState([])
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const refreshContracts = useCallback(async () => {
    try {
      setIsLoading(true)
      const { data } = await ContractManagementRequest.getContracts({
        page: 0,
        size: 1000,
      })
      const normalizedContracts = (data?.content || []).map(mapApiContractToUi)
      setContracts(normalizedContracts)
    } catch (error) {
      console.error(error)
      setContracts([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshContracts()
  }, [refreshContracts])

  const filteredContracts = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase()
    return contracts.filter(contract => {
      const isMatchedStatus =
        !statusFilter ||
        String(contract.status || '').toLowerCase() ===
          String(statusFilter || '').toLowerCase()
      const searchableText = [
        contract.contractNo,
        contract.customerName,
        contract.supplierName,
      ]
        .join(' ')
        .toLowerCase()

      const isMatchedKeyword =
        !normalizedKeyword || searchableText.includes(normalizedKeyword)

      return isMatchedStatus && isMatchedKeyword
    })
  }, [contracts, keyword, statusFilter])

  console.log(JSON.stringify(filteredContracts))

  const handleDeleteContract = useCallback(
    async contractId => {
      try {
        await ContractManagementRequest.deleteContract(contractId)
        message.success('Đã xóa hợp đồng thành công')
        refreshContracts()
      } catch (error) {
        console.error(error)
      }
    },
    [refreshContracts]
  )

  const handleExportExcel = useCallback(() => {
    try {
      setIsExporting(true)
      const excelHeaders = [
        'Số hợp đồng',
        'Bên B',
        'Ngày ký',
        'Ngày hiệu lực',
        'Tổng giá trị',
        'Số dịch vụ',
        'Số tài liệu',
        'Trạng thái',
      ]

      const excelRows = filteredContracts.map(contract => {
        const statusText = getStatusInfo(contract.status).label

        return [
          contract.contractNo || '',
          contract.customerName || '',
          formatContractDate(contract.signedDate),
          formatContractDate(contract.effectiveDate),
          contract.totalValue || 0,
          contract.totalService || contract.services || 0,
          contract.totalFile || contract.documents || 0,
          statusText || '',
        ]
      })

      utils.exportExcel(
        [excelHeaders, ...excelRows],
        `danh-sach-hop-dong-${Date.now()}`
      )
      message.success('Xuất file dữ liệu thành công')
    } catch (error) {
      console.error(error)
      message.error('Không thể xuất dữ liệu hợp đồng')
    } finally {
      setIsExporting(false)
    }
  }, [filteredContracts])

  const columns = [
    {
      title: 'Số hợp đồng',
      width: 170,
      render: record => (
        <strong style={{ color: '#2c65ac' }}>{record.contractNo}</strong>
      ),
    },
    // {
    //   title: 'Bên A (Nhà cung cấp)',
    //   width: 220,
    //   render: record => <span>{record.supplierName}</span>,
    // },
    {
      title: 'Bên B (Khách hàng)',
      with: 120,
      render: record => <span>{record.customerName}</span>,
    },
    {
      title: 'Ngày ký',
      width: 120,
      render: record => formatContractDate(record.signedDate),
    },
    {
      title: 'Ngày hiệu lực',
      width: 130,
      render: record => formatContractDate(record.effectiveDate),
    },
    {
      title: 'Tổng giá trị',
      width: 170,
      align: 'right',
      render: record => (
        <strong style={{ color: '#3f8600' }}>
          {formatCurrencyVN(record.totalValue)}
        </strong>
      ),
    },
    {
      title: 'Số dịch vụ',
      width: 110,
      align: 'center',
      render: record => <Tag color="cyan">{record.totalService || 0}</Tag>,
    },
    {
      title: 'Số tài liệu',
      width: 110,
      align: 'center',
      render: record => <Tag color="default">{record.totalFile || 0}</Tag>,
    },
    {
      title: 'Trạng thái',
      width: 130,
      align: 'center',
      render: (_, record) => {
        const statusInfo = getStatusInfo(record.status)
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
      },
    },
    {
      title: 'Thao tác',
      width: 150,
      fixed: 'right',
      align: 'center',
      render: record => (
        <Space size={4}>
          {/* <Tooltip title="Xem chi tiết">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={event => {
                event.stopPropagation()
                history.push(`/utility/contracts/view/${record.id}`)
              }}
            />
          </Tooltip> */}

          <AccessControlAction aclActionType={ACL_ACTION_TYPE.edit__CONTRACT}>
            <Tooltip title="Chỉnh sửa">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={event => {
                  event.stopPropagation()
                  history.push(`/utility/contracts/edit/${record.id}`)
                }}
              />
            </Tooltip>
          </AccessControlAction>

          <AccessControlAction
            aclActionType={ACL_ACTION_TYPE.delete__CONTRACT}>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa hợp đồng này không?"
              okText="Xóa"
              cancelText="Hủy"
              onConfirm={event => {
                event?.stopPropagation()
                handleDeleteContract(record.id)
              }}>
              <Tooltip title="Xóa hợp đồng">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={event => event.stopPropagation()}
                />
              </Tooltip>
            </Popconfirm>
          </AccessControlAction>
        </Space>
      ),
    },
  ]

  return (
    <DashboardLayout>
      <Helmet>
        <title>Quản lý hợp đồng | {systemName} Portal</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title={'Quản lý hợp đồng'}
        hiddenGoBack>
        <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__CONTRACT}>
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={() => history.push('/utility/contracts/create')}>
            Tạo hợp đồng mới
          </Button>
        </AccessControlAction>
      </PageTitle>
      <ContentBlockWrapper>
        <div style={{ marginBottom: 16 }}>
          <Space wrap size={12}>
            <Input
              allowClear
              value={keyword}
              onChange={event => setKeyword(event.target.value)}
              style={{ width: 340 }}
              placeholder="Tìm theo số hợp đồng, tên khách hàng..."
              prefix={<SearchOutlined />}
            />
            <Select
              allowClear
              value={statusFilter || undefined}
              onChange={value => setStatusFilter(value || '')}
              style={{ width: 220 }}
              placeholder="Tất cả trạng thái">
              {CONTRACT_STATUS_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              loading={isExporting}>
              Xuất dữ liệu
            </Button>
          </Space>
        </div>
        <TableComponent
          loading={isLoading}
          rowKey={record => record.id}
          columns={columns}
          dataSource={filteredContracts}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `Hiển thị ${range[0]} - ${range[1]} trong tổng ${total} hợp đồng`,
          }}
          scroll={{ x: 1450 }}
          onRow={record => {
            return {
              onClick: () =>
                history.push(`/utility/contracts/view/${record.id}`),
            }
          }}
        />
      </ContentBlockWrapper>
    </DashboardLayout>
  )
}

export default memo(ContractManagementListPage)
